'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';

interface PunchInFABProps {
  onPunchIn: () => void;
  isPunchedIn: boolean;
  canPunch?: boolean;
}

const STORAGE_KEY = 'hrm_punch_fab_position';
const FAB_SIZE = 64;
const EDGE_PADDING = 16;
const DRAG_THRESHOLD_PX = 8;
const DEFAULT_POSITION = { xPct: 92, yPct: 88 };

type StoredPosition = { xPct: number; yPct: number };

function loadStoredPosition(): StoredPosition {
  if (typeof window === 'undefined') return DEFAULT_POSITION;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_POSITION;
    const parsed = JSON.parse(raw) as StoredPosition;
    if (typeof parsed.xPct === 'number' && typeof parsed.yPct === 'number') {
      return {
        xPct: Math.min(100, Math.max(0, parsed.xPct)),
        yPct: Math.min(100, Math.max(0, parsed.yPct)),
      };
    }
  } catch {
    /* ignore corrupt storage */
  }
  return DEFAULT_POSITION;
}

function getMaxOffset() {
  if (typeof window === 'undefined') {
    return { maxX: 0, maxY: 0 };
  }
  return {
    maxX: Math.max(0, window.innerWidth - FAB_SIZE - EDGE_PADDING * 2),
    maxY: Math.max(0, window.innerHeight - FAB_SIZE - EDGE_PADDING * 2),
  };
}

function percentToPixels(xPct: number, yPct: number) {
  const { maxX, maxY } = getMaxOffset();
  return {
    x: EDGE_PADDING + (xPct / 100) * maxX,
    y: EDGE_PADDING + (yPct / 100) * maxY,
  };
}

function pixelsToPercent(x: number, y: number): StoredPosition {
  const { maxX, maxY } = getMaxOffset();
  return {
    xPct: maxX > 0 ? ((x - EDGE_PADDING) / maxX) * 100 : DEFAULT_POSITION.xPct,
    yPct: maxY > 0 ? ((y - EDGE_PADDING) / maxY) * 100 : DEFAULT_POSITION.yPct,
  };
}

function clampPosition(x: number, y: number) {
  const { maxX, maxY } = getMaxOffset();
  return {
    x: Math.min(EDGE_PADDING + maxX, Math.max(EDGE_PADDING, x)),
    y: Math.min(EDGE_PADDING + maxY, Math.max(EDGE_PADDING, y)),
  };
}

const PunchInFAB: React.FC<PunchInFABProps> = ({
  onPunchIn,
  isPunchedIn,
  canPunch = true,
}) => {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const storedPctRef = useRef<StoredPosition>(DEFAULT_POSITION);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    moved: boolean;
  } | null>(null);
  const positionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const applyStoredPercent = useCallback(() => {
    const px = percentToPixels(storedPctRef.current.xPct, storedPctRef.current.yPct);
    positionRef.current = px;
    setPosition(px);
  }, []);

  useEffect(() => {
    storedPctRef.current = loadStoredPosition();
    applyStoredPercent();
  }, [applyStoredPercent]);

  useEffect(() => {
    const onResize = () => applyStoredPercent();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [applyStoredPercent]);

  const persistPosition = useCallback((x: number, y: number) => {
    const pct = pixelsToPercent(x, y);
    storedPctRef.current = pct;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pct));
    } catch {
      /* private mode / quota */
    }
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const { x, y } = positionRef.current;
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: x,
      origY: y,
      moved: false,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) {
      drag.moved = true;
      setIsDragging(true);
    }

    const next = clampPosition(drag.origX + dx, drag.origY + dy);
    positionRef.current = next;
    setPosition(next);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    const wasDrag = drag.moved;
    dragRef.current = null;
    setIsDragging(false);

    const { x, y } = positionRef.current;
    persistPosition(x, y);

    if (!wasDrag) {
      onPunchIn();
    }
  };

  const handlePointerCancel = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  if (!canPunch && !isPunchedIn) return null;
  if (!position) return null;

  return (
    <div
      className="fixed z-[60] group touch-none select-none"
      style={{ left: position.x, top: position.y, width: FAB_SIZE, height: FAB_SIZE }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      role="presentation"
    >
      {/* Label on hover (hidden while dragging) */}
      <div
        className={`absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-white dark:bg-slate-800 px-4 py-2 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 transition-all duration-300 pointer-events-none whitespace-nowrap ${
          isDragging
            ? 'opacity-0'
            : 'opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0'
        }`}
      >
        <p className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-2">
          <span
            className={`w-2 h-2 rounded-full ${isPunchedIn ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'}`}
          />
          {isPunchedIn ? 'End your shift?' : 'Ready to Punch In?'}
        </p>
        <p className="text-[9px] text-slate-400 mt-1 normal-case tracking-normal font-medium">
          Drag to move · Click to open
        </p>
      </div>

      {/* Main FAB */}
      <button
        type="button"
        aria-label={isPunchedIn ? 'Punch out' : 'Punch in'}
        className={`relative w-full h-full rounded-2xl text-white shadow-2xl flex items-center justify-center transition-all duration-200 ${
          isDragging ? 'cursor-grabbing scale-105' : 'cursor-grab active:scale-95'
        } ${
          isPunchedIn
            ? 'bg-slate-900 dark:bg-white dark:text-slate-900 shadow-slate-500/20'
            : 'bg-[var(--hrm-primary)] shadow-[0_8px_24px_rgba(255,159,67,0.35)]'
        }`}
        onClick={(e) => e.preventDefault()}
      >
        {!isPunchedIn && !isDragging && (
          <div className="absolute inset-0 rounded-2xl bg-[var(--hrm-primary)] animate-ping opacity-10 group-hover:opacity-30 pointer-events-none" />
        )}

        <div
          className={`absolute inset-2 rounded-xl border pointer-events-none ${
            isPunchedIn ? 'border-slate-500/20' : 'border-white/20'
          }`}
        />

        <div className="relative flex flex-col items-center pointer-events-none">
          <i
            className={`${isPunchedIn ? 'ri-login-box-line' : 'ri-fingerprint-line'} text-2xl md:text-3xl`}
          />
          <span className="text-[8px] font-black uppercase tracking-tighter mt-0.5 hidden md:block">
            {isPunchedIn ? 'Punch Out' : 'Punch In'}
          </span>
        </div>

      </button>
    </div>
  );
};

export default PunchInFAB;
